import errorLogger, { BUGSNAG_STATUSES } from '@picsart/growth-rc/services/errorLogger';

import { BATCH_ERROR } from 'pages/Batch/configs';

import { BatchDataStatus } from 'configs/constants';

import loadingImage from 'utils/images/loadingImage';
import getErrorMessage from 'utils/requests/error';

interface IProcessData {
  key: string;
  status?: BatchDataStatus;
  result: {
    src: string;
    width?: number;
    height?: number;
  };
  pendingResult?: {
    src: string;
    removedBgSrc?: string;
    mask?: string;
    width?: number;
    height?: number;
    enhancedSrc?: string;
  };
  doNotProcess?: boolean;
}

export interface IProcessResult {
  // TODO HTMLCanvasElement will be deleted.
  result: string | HTMLCanvasElement;
  blob?: Blob;
  type?: string;
}

interface IProcessBatch<T> {
  callback: (data: T) => void;
  usePending?: boolean;
  useEnhanced?: boolean;
  saveAsEnhanced?: boolean;
  processFn: (image: HTMLImageElement, isCancel: () => boolean, itemKey: string) => Promise<IProcessResult>;
  srcArray: T[];
}

interface IProcess<T> extends Omit<IProcessBatch<T>, 'srcArray' | 'concurrency'> {
  data: T;
}

type IProcessInDepend<T> = Omit<IProcess<T>, 'callback'>;

class Toolchain {
  private canceled = false;

  public async process<T extends IProcessData>({ srcArray, ...restProps }: IProcessBatch<T>) {
    const promises = srcArray.map(data => {
      const doNotProcess = data.status === BatchDataStatus.ERROR;
      return this.processItem({ ...restProps, data: { ...data, doNotProcess } });
    });

    await Promise.all(promises);
  }

  public async processItem<T extends IProcessData>({ data, callback, ...restProps }: IProcess<T>) {
    if (this.canceled) return;
    if (data.doNotProcess) return callback({ ...data, status: BatchDataStatus.ERROR });

    try {
      const pendingResult = await this.processInDepend({ ...restProps, data });
      if (this.canceled) return;
      if (!pendingResult.src) {
        throw new Error('Pending result or src is undefined or invalid');
      }

      callback({ ...data, status: BatchDataStatus.DONE, pendingResult });
    } catch (error) {
      if (!this.canceled) {
        errorLogger.trigger(error as Error, BUGSNAG_STATUSES.ERROR);
        callback({
          ...data,
          status: BatchDataStatus.ERROR,
          errorType: BATCH_ERROR[getErrorMessage(error) as BATCH_ERROR] || BATCH_ERROR.serverError,
        });
      }
    }
  }

  private async processInDepend<T extends IProcessData>({ data, processFn, usePending = false, useEnhanced = false, saveAsEnhanced = false }: IProcessInDepend<T>) {
    const { result, pendingResult } = data;
    const pending = pendingResult;
    // in case there is no pending results yet, use result src
    const pendingSrc = pending?.mask || pending?.src || result.src;
    let src: string;
    if (useEnhanced && pending?.enhancedSrc) {
      src = pending.enhancedSrc;
    } else if (usePending) {
      src = pendingSrc;
    } else {
      src = result.src;
    }

    const image = await loadingImage(src);
    const processedData = await processFn(image, () => this.canceled, data.key);
    const { width, height } = await this.getDimensions(processedData.result);

    return {
      type: processedData?.type,
      removedBgSrc: processedData.result,
      src: processedData.result,
      width,
      height,
      enhancedSrc: saveAsEnhanced ? processedData.result : pending?.enhancedSrc,
    };
  }

  private async getDimensions(src: string | HTMLCanvasElement): Promise<{ width: number; height: number }> {
    if (src instanceof HTMLCanvasElement) return { width: src.width, height: src.height };
    const img = await loadingImage(src);
    return { width: img.naturalWidth, height: img.naturalHeight };
  }

  public cancel() {
    this.canceled = true;
  }
}

export default Toolchain;
